import { Container } from 'pixi.js';
import { GAME } from '@/config/game';
import type { ChamberSize } from '@/config/maps';
import type { MoteStyle, ThemeDef } from '@/config/themes';
import type { Rng } from '@/core/rng';
import { Arena } from '@/arena/arena';
import { ArenaView } from '@/arena/arena-view';
import type { Piece } from '@/arena/geometry';
import type { ArenaLayout } from '@/arena/layouts';
import type { Boss } from '@/bosses/boss-base';
import { BulletSystem } from '@/entities/bullets';
import { DropSystem } from '@/entities/drops';
import { GemSystem } from '@/entities/gems';
import { ItemDropSystem } from '@/entities/items';
import { MinionSystem } from '@/entities/minions';
import { Player } from '@/entities/player';
import { Portal } from '@/entities/portal';
import { AmbientMotes } from '@/render/ambient-motes';
import { Camera } from '@/render/camera';
import { DamageNumberSystem } from '@/render/damage-numbers';
import { FxSystem } from '@/render/fx';
import { TelegraphSystem } from '@/render/telegraphs';

/**
 * The fight scene: every in-arena system, the current arena + boss, and
 * the world layer stack. The only run-side module that touches Pixi
 * containers — run/ and combat/ logic goes through these methods.
 */
export class World {
  readonly camera = new Camera();
  readonly player = new Player();
  readonly bullets = new BulletSystem();
  readonly drops = new DropSystem();
  readonly gems = new GemSystem();
  readonly minions = new MinionSystem();
  readonly portal = new Portal();
  readonly fx = new FxSystem();
  readonly telegraphs = new TelegraphSystem();
  readonly damageNumbers = new DamageNumberSystem();
  readonly items = new ItemDropSystem();
  readonly motes = new AmbientMotes();
  arena!: Arena;
  arenaView: ArenaView | null = null;
  boss: Boss | null = null;
  /** Theme motes wait for the first render (camera centre known by then) */
  private pendingMotes: MoteStyle | null = null;

  private readonly arenaSlot = new Container();
  private readonly bossSlot = new Container();
  private readonly shadowLayer = new Container();

  constructor(layer: Container) {
    // portal.sprite lives OUTSIDE arenaSlot: buildArena destroys arenaSlot
    // children, and destroying the persistent portal sprite crashes the renderer
    // Readability stack (docs/12 E3): ENEMY shots on top of everything
    layer.addChild(
      this.arenaSlot, this.motes.view, this.portal.sprite, this.shadowLayer, this.telegraphs.view,
      this.drops.view, this.items.view, this.gems.view, this.bossSlot, this.minions.view,
      this.bullets.friendlyView, this.fx.view, this.player.view, this.damageNumbers.view,
      this.bullets.enemyView,
    );
    this.shadowLayer.addChild(this.player.shadow);
    this.camera.setBounds(0, 0, GAME.arena.width, GAME.arena.height);
  }

  /**
   * Tear the old room down (fight systems too) and build `layout` in
   * `theme`. `rng` is the room's DECOR stream (never the run rng);
   * `chamber` sizes an advance route's boss chamber.
   */
  buildArena(rng: Rng, layout: ArenaLayout, theme: ThemeDef, chamber: ChamberSize): Arena {
    this.arenaSlot.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.bossSlot.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.drops.clear();
    this.minions.clear();
    this.bullets.clearAll();
    this.gems.clear();
    this.fx.clear();
    this.damageNumbers.clear();

    this.arena = new Arena(layout, chamber);
    this.arenaView = new ArenaView(this.arena, theme, rng);
    this.arenaSlot.addChild(this.arenaView.view);
    const b = this.arena.bounds;
    this.camera.setBounds(b.x, b.y, b.w, b.h);
    this.confineMinions(this.arena.fight);
    this.pendingMotes = theme.mote;
    this.items.clear();
    return this.arena;
  }

  /** Minions stay inside `piece` (the arena, a route segment or the chamber). */
  confineMinions(piece: Piece): void {
    const o = piece.outer;
    this.minions.setBounds(o.w, o.h, o.x, o.y);
  }

  /** Make `boss` the fight's boss and put it on stage. */
  mountBoss(boss: Boss): void {
    this.boss = boss;
    this.bossSlot.addChild(boss.view);
  }

  /** Held offstage during the waves / gone after death (docs/12 C8). */
  setBossVisible(visible: boolean): void {
    if (this.boss) this.boss.view.visible = visible;
  }

  render(alpha: number): void {
    const cam = this.camera;
    const timeS = performance.now() / 1000;
    if (this.pendingMotes && cam.viewW > 0) {
      this.motes.setStyle(this.pendingMotes, cam.centerX, cam.centerY, cam.viewW, cam.viewH);
      this.pendingMotes = null;
    }
    this.arenaView?.render(cam.centerX, cam.centerY, cam.viewW, cam.viewH, timeS);
    this.motes.render(cam.centerX, cam.centerY, cam.viewW, cam.viewH, timeS);
    this.player.render(alpha);
    this.bullets.render(alpha);
    this.telegraphs.render();
    this.drops.render(this.player.x, this.player.y);
    this.items.render();
    this.gems.render();
    this.portal.render();
    this.boss?.render(alpha);
    this.minions.render();
  }
}
